import React, {
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
  useLayoutEffect
} from 'react';
import { MentionsInput, Mention } from 'react-mentions';
import Uppy from '@uppy/core';
import {
  Avatar,
  Button,
  Checkbox,
  Col,
  Dropdown,
  MenuProps,
  Row,
  Tooltip,
  Input
} from 'antd';
import DrawingToolSvg from '@assets/icons/drawing-tool.svg';
import { ReactComponent as DoggySvg } from '@assets/icons/doggy.svg';
import { ReactComponent as SendSvg } from '@assets/icons/send.svg';
import { ReactComponent as CrossSvg } from '@assets/icons/cross-sm.svg';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';
import { ReactComponent as AttachSvg } from '@assets/icons/Attach.svg';
import { ReactComponent as UploadFilesSvg } from '@assets/icons/upload-campaign-files.svg';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { TextAreaRef } from 'antd/lib/input/TextArea';
import {
  getFrameAtTime,
  getSmpteTimecodeAtTime,
  isAudioVideo
} from '@helpers/avInfo';
import { formatDuration } from '@helpers/formatDuration';
import { useSelectedMVComment } from '@hooks';
import {
  AssetVersionCommentGroupMentionDto,
  AssetVersionCommentMentionDto,
  AttachmentDto
} from '@api/Api';
import {
  toggleSelectFilesModal,
  toggleSingInExternalReviewerModal
} from '@redux/actions/modalAction';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import UserAvatar from '@components/UserAvatar';
import { useUppyState } from '@hooks/useUppyState';
import { useAuth } from '@hooks/useAuth';
import { usePreviousInputValue } from '@hooks/usePreviousInputValue';
import useHasAnnotations from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useHasAnnotations';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import { hashedColor } from '@helpers/hashedColor';
import CommentAttachments from '@pages/MediaViewer/Comments/components/CommentAttachments';
import { combineUserName } from '@helpers/combineUserName';

import './CommentEditor.less';

const { TextArea } = Input;

type CommentEditorProps = {
  commentMentions: boolean;
  comment?: any;
  autoFocus?: boolean;
  setEditCommentId: (a: string | null) => void;
  editCommentId: any;
  editorFor: 'new' | 'reply' | 'edit';
  replyToCommentId: string | null;
  setReplyToCommentId: (a: string | null) => void;
  isFocused: boolean;
  onSubmitEditor: (value: SubmitEditorValues) => Promise<void>;
  isCheckboxChecked: boolean;
  setIsCheckboxChecked: (a: boolean) => void;
  checkboxValueType?: 'timeCode' | 'pageNumber' | null;
  potentialMentions: AssetVersionCommentMentionDto[];
  potentialGroupMentions: AssetVersionCommentGroupMentionDto[];
  searchPotentialMentions: (search: string) => void;
  uppy: Uppy;
  onRequestExpand?: () => void;
  disableAttachments?: boolean;
};

type SendCommentBtnProps = {
  isDisabled: boolean;
  editCommentId: string | null;
  setEditCommentId: (a: string | null) => void;
  editorFor: 'new' | 'reply' | 'edit';
  replyToCommentId: string | null;
  setReplyToCommentId: (a: string | null) => void;
  onSubmit: any;
};

export type SubmitEditorValues = {
  text: string;
  rawText: string;
  mentions: string[];
  groupMentions: string[];
  idOrParentId: string;
  documentHighlight?: string | null;
  attachmentsLeft?: AttachmentDto[];
  documentHighlightRects?:
    | { top: number; left: number; width: number; height: number }[]
    | null;
  documentHighlightReplacement?: string | null;
};

function SendCommentBtn({
  isDisabled,
  editCommentId,
  setEditCommentId,
  editorFor,
  replyToCommentId,
  setReplyToCommentId,
  onSubmit
}: SendCommentBtnProps) {
  const { selectComment, setDocumentHighlight, setCanvasMode } =
    useAssetViewerState();
  const [loading, setLoading] = useState<boolean>(false);

  const onCreateComment = useCallback(
    async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      try {
        e.stopPropagation();
        if (loading) return;
        if ((!editCommentId && !replyToCommentId) || isDisabled) return;
        setLoading(true);
        await onSubmit();
      } catch (e) {
        console.warn(e, '-> onCreateComment');
      } finally {
        setLoading(false);
      }
    },
    [loading, editCommentId, replyToCommentId, isDisabled, setLoading, onSubmit]
  );

  const onEditComment = useCallback(
    async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      try {
        e.preventDefault();
        e.stopPropagation();
        setLoading(true);
        await onSubmit();
      } catch (e) {
        console.warn(e, '-> onEditComment');
        setLoading(false);
      }
    },
    [setLoading, onSubmit]
  );

  const onCancel = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation();
      setEditCommentId(null);
      setReplyToCommentId(null);
      selectComment(null);
      setDocumentHighlight(null);
      setCanvasMode('view');
    },
    [
      setEditCommentId,
      setReplyToCommentId,
      selectComment,
      setDocumentHighlight,
      setCanvasMode
    ]
  );

  if (editorFor !== 'edit')
    return (
      <div className={classNames('send_comment')}>
        <Button
          type="text"
          htmlType="submit"
          disabled={isDisabled}
          loading={loading}
          onClick={onCreateComment}
          className="create_comment_btn"
        >
          <SendSvg className="create_comment_btn-icon" />
        </Button>
      </div>
    );
  return (
    <div className="send_comment">
      <Button
        type="text"
        className="rich_text_form_btn"
        htmlType="button"
        onClick={onCancel}
      >
        Cancel
      </Button>

      <Button
        type="primary"
        className="rich_text_form_btn"
        htmlType="submit"
        disabled={isDisabled}
        loading={loading}
        onClick={onEditComment}
      >
        Save
      </Button>
    </div>
  );
}

function CommentEditor({
  commentMentions,
  comment = null,
  setEditCommentId,
  autoFocus = true,
  editCommentId,
  editorFor,
  replyToCommentId,
  setReplyToCommentId,
  isFocused,
  onSubmitEditor,
  isCheckboxChecked,
  setIsCheckboxChecked,
  checkboxValueType = null,
  potentialMentions,
  potentialGroupMentions,
  searchPotentialMentions,
  uppy,
  onRequestExpand,
  disableAttachments = false
}: CommentEditorProps) {
  const hasAnnotations = useHasAnnotations();
  const { onChangePrevValues, onResetPrevValues } = usePreviousInputValue();
  const [onAddNewMention, setOnAddNewMention] = useState<boolean>(false);
  const [commentText, setCommentText] = useState<any>('');
  const [documentHighlightReplacement, setDocumentHighlightReplacement] =
    useState<string | null>(null);
  const [isCommentSending, setIsCommentSending] = useState<boolean>(false);
  const [commentAttachments, setCommentAttachments] = useState<AttachmentDto[]>(
    []
  );
  const [isDragOver, setIsDragOver] = useState(false);
  const dispatch = useDispatch();
  const { user } = useAuth();
  const inputRef = useRef<any>();
  const {
    playerTime,
    documentHighlight,
    isCommentFieldFocused,
    activePageNumber,
    isTimecodeChecked,
    timeFormat,
    selectedVersion,
    selectComment,
    setCanvasMode,
    setDocumentHighlight,
    setIsTimecodeChecked,
    setCommentText: setCommentTextState,
    setIsCommentFieldFocused
  } = useAssetViewerState();
  const hasDocumentHighlight = !!documentHighlight.rects?.length;

  const isCommentFieldFocusedRef = useRef(isCommentFieldFocused);
  isCommentFieldFocusedRef.current = isCommentFieldFocused;
  const uppyState = useUppyState(uppy);

  const commentDetails = useSelectedMVComment();

  const commentFiles = useMemo(
    () => {
      const uppyFiles = (uppy?.getFiles?.() || []).map((item) => {
        return {
          id: item.id,
          name: item.name,
          fileType: item.type || '',
          mimeType: item.type || '',
          size: item.size,
          url:
            item.preview ||
            item?.meta?.previewUrl ||
            URL.createObjectURL(item.data) ||
            '',
          urlExpiresAt: item.meta.relativePath || '',
          progress: item.progress?.uploadStarted
            ? item.progress.percentage
            : undefined
        };
      });
      return [...commentAttachments, ...uppyFiles];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uppy, commentAttachments, uppyState]
  );

  const mentions = useMemo(
    () => [
      ...potentialGroupMentions.map((el) => ({
        type: 'group' as const,
        id: el.id,
        name: el.name,
        description: '',
        picture: (
          <Avatar
            size={40}
            shape="square"
            style={{ background: hashedColor(el.id, 'memberGroupAvatar') }}
          >
            {el.name[0].toUpperCase()}
          </Avatar>
        )
      })),
      ...potentialMentions.map((el) => ({
        type: 'user' as const,
        id: el.id,
        name: combineUserName(el),
        description: el.email,
        picture: (
          <UserAvatar
            isActive={true}
            size="large"
            src={el.picture.url}
            userEmail={el.email}
            userName={combineUserName(el)}
          />
        )
      }))
    ],
    [potentialGroupMentions, potentialMentions]
  );

  const avatarIcon = useMemo(() => {
    if (!user) return '';
    if (isFocused && (hasDocumentHighlight || hasAnnotations))
      return DrawingToolSvg as unknown;
    return user.pictureUrl || '';
  }, [user, isFocused, hasAnnotations, hasDocumentHighlight]);

  const [selectedMentions, setSelectedMentions] = useState<
    { id: string; name: string; type: 'user' | 'group' }[]
  >([]);

  useEffect(() => {
    if (!inputRef.current) return;
    if (!isCommentFieldFocused) return;

    inputRef.current.focus();
  }, [isCommentFieldFocused]);

  const highlightInputRef = useRef<TextAreaRef>(null);
  useEffect(() => {
    if (editorFor !== 'new') return;
    if (!highlightInputRef.current) return;
    if (!documentHighlight.rects?.length) return;

    highlightInputRef.current.focus();
  }, [documentHighlight.rects?.length, editorFor]);

  useEffect(() => {
    setIsCheckboxChecked(isTimecodeChecked || hasDocumentHighlight);
  }, [isTimecodeChecked, hasDocumentHighlight, setIsCheckboxChecked]);

  useEffect(() => {
    if (editorFor === 'edit') {
      const replacementText =
        commentDetails?.documentHighlight !== documentHighlight.text
          ? documentHighlight.text
          : commentDetails?.documentHighlightReplacement;
      setDocumentHighlightReplacement(replacementText || '');
      return;
    }
    setDocumentHighlightReplacement(documentHighlight.text);
  }, [
    documentHighlight.text,
    commentDetails,
    editorFor,
    setDocumentHighlightReplacement,
    dispatch
  ]);

  useEffect(() => {
    if (!hasAnnotations) return;

    setIsTimecodeChecked(true);
    setIsCheckboxChecked(true);
  }, [hasAnnotations, dispatch, setIsCheckboxChecked]);

  useEffect(() => {
    if (!commentDetails?.id) return;
    if (editorFor !== 'edit') return;
    if (comment?.id !== commentDetails?.id) return;
    setSelectedMentions([
      ...commentDetails.mentions.map((el) => ({
        id: el.id,
        name: combineUserName(el),
        type: 'user' as const
      })),
      ...commentDetails.groupMentions.map((el) => ({
        id: el.id,
        name: el.name,
        type: 'group' as const
      }))
    ]);
    const _commentText = commentDetails.text
      .replace(/<br[^>]*>/gi, '\n')
      .replace(/<[^>]*>?/gm, '');
    setCommentText(_commentText);
    setCommentTextState(_commentText);
  }, [commentDetails, editorFor, comment?.id, setCommentTextState]);

  const onSubmit = async () => {
    const text = commentText.replace(/\n/g, '<br />');
    let rawText = commentText.replace(/\n/g, '<br />');

    const id = editCommentId || replyToCommentId;
    selectedMentions.forEach((el: any) => {
      rawText = rawText.replace(`{{${el.id}}}`, `@${el.name}`);
    });

    await onSubmitEditor({
      text,
      rawText,
      mentions: selectedMentions
        .filter((el) => el.type === 'user')
        .filter((el) => text.includes(el.id))
        .map((el) => el.id),
      groupMentions: selectedMentions
        .filter((el) => el.type === 'group')
        .filter((el) => text.includes(el.id))
        .map((el) => el.id),
      idOrParentId: id,
      documentHighlight: documentHighlight.text,
      documentHighlightRects: documentHighlight.rects,
      documentHighlightReplacement,
      attachmentsLeft: commentAttachments
    });
    uppy.setMeta({
      commentId: null
    });
    uppy.cancelAll();
    setCommentAttachments([]);
    setEditCommentId(null);
    setReplyToCommentId(null);
    setSelectedMentions([]);
    setCommentText('');
    setDocumentHighlightReplacement('');
    onResetPrevValues();
    setCommentTextState(null);
  };

  const onDeleteDocumentHighlight = () => {
    setDocumentHighlightReplacement('');
    setDocumentHighlight(null);
  };

  const mentionsCallbackRef = useRef<(mentions?: any) => void>();
  useLayoutEffect(() => {
    mentionsCallbackRef.current?.(mentions);
  }, [mentions]);

  const usersMenu = useMemo<MenuProps>(
    () => ({
      className: 'comment_mentions',
      selectable: false,
      items: mentions.map((item) => ({
        key: item.id,
        className: 'rich_text_form_invite-item',
        onClick: ({ key }) => {
          const selectedMentions = mentions.filter((el) => el.id === key);
          setSelectedMentions((prev) => [...prev, ...selectedMentions]);
          const text = `${commentText} {{${key}}}`;
          setCommentText(text);
          setCommentTextState(text);
        },
        label: (
          <Row wrap={false} gutter={16} align="middle">
            <Col>{item.picture}</Col>
            <Col flex="auto">
              {item && (
                <>
                  <div className="rich_text_form_invite-name">{item.name}</div>
                  {item.description && (
                    <div className="rich_text_form_invite-email">
                      {item.description}
                    </div>
                  )}
                </>
              )}
            </Col>
          </Row>
        )
      }))
    }),
    [mentions, commentText, setCommentText, setCommentTextState]
  );

  const renderCommentTime = useCallback(
    (time: number) => {
      const metadata = selectedVersion?.metadata;

      if (timeFormat === 'frames') {
        const frame = getFrameAtTime(time, metadata);
        if (frame !== null) return frame;
      }
      if (timeFormat === 'timecode') {
        const videoFrames = isAudioVideo(metadata)
          ? metadata.info.avInfo?.video?.frames
          : undefined;
        const avTimecode = isAudioVideo(metadata)
          ? metadata.info.avInfo?.timecode
          : undefined;
        const timecode = getSmpteTimecodeAtTime(time, avTimecode, videoFrames);
        if (timecode !== null) return timecode;
      }

      return formatDuration(time);
    },
    [selectedVersion?.metadata, timeFormat]
  );

  const onAddFiles = useCallback(
    (files: File[]) => {
      uppy.addFiles(
        files.map((file) => ({
          name: file.name,
          type: file.type,
          data: file,
          meta: {
            relativePath: new Date().toISOString()
          }
        }))
      );
    },
    [uppy]
  );

  const onOpenAttachmentModal = useCallback(() => {
    dispatch(
      toggleSelectFilesModal({
        key: 'media-viewer-comment',
        visible: true,
        onFinish: (files) => onAddFiles(files)
      })
    );
  }, [onAddFiles, dispatch]);

  useEffect(() => {
    setCommentAttachments(commentDetails?.attachments || []);
  }, [commentDetails?.attachments, setCommentAttachments]);

  useEffect(() => {
    if (editorFor === 'edit') {
      uppy.setMeta({
        commentId: commentDetails?.id
      });
    }
    return () => {
      uppy.setMeta({
        commentId: null
      });
      uppy.cancelAll();
    };
  }, [commentDetails?.id, editorFor, uppy, setCommentAttachments]);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!isFocused) return;
      event.preventDefault();
      setIsDragOver(false);
      const droppedFiles = Array.from(event.dataTransfer.files);
      onAddFiles(droppedFiles);
    },
    [isFocused, onAddFiles, setIsDragOver]
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (!isFocused) return;
      event.stopPropagation();
      event.preventDefault();
      setIsDragOver(true);
    },
    [isFocused, setIsDragOver]
  );

  const handleDragLeave = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;
      const isOutside =
        x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom;

      if (isOutside) setIsDragOver(false);
    },
    [setIsDragOver]
  );

  return (
    <div
      className="comment_editor tour-review-asset-comments tour-text-highlight-comments tour-time-range-review-audio-video"
      onDrop={disableAttachments ? undefined : handleDrop}
      onDragOver={disableAttachments ? undefined : handleDragOver}
      onDragLeave={disableAttachments ? undefined : handleDragLeave}
    >
      {!disableAttachments && isDragOver && (
        <div className="comment_editor-drag">
          <UploadFilesSvg className="icon" />
          <div>Drop to attach file</div>
        </div>
      )}
      <div
        className={classNames(
          editCommentId && 'comment_editor_container-focused',
          'comment_editor_container'
        )}
        onClick={() => {
          if (!user) return;
          if (editorFor !== 'new') return;
          if (!isFocused && editorFor === 'new') {
            setIsTimecodeChecked(true);
            if (!documentHighlight.rects?.length) {
              setDocumentHighlight(null);
            }
            onRequestExpand?.();
          }
          setEditCommentId('new');
          selectComment('new');
          setCanvasMode('edit');
          setReplyToCommentId(null);
        }}
      >
        {!user && (
          <div
            onClick={() => {
              dispatch(
                toggleSingInExternalReviewerModal({
                  visible: true
                })
              );
            }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1
            }}
          />
        )}
        {hasDocumentHighlight && isFocused && (
          <div className="comment_text-highlight">
            <TextArea
              ref={highlightInputRef}
              prefixCls="comment_text-highlight-input"
              autoSize
              value={documentHighlightReplacement || ''}
              placeholder={documentHighlight.text || ''}
              onKeyDown={(e) => {
                const cancelHighlight =
                  e.key === 'Escape' ||
                  (e.key === 'Backspace' && !(e.target as any).value);
                if (!cancelHighlight) return;
                e.preventDefault();
                setDocumentHighlightReplacement(null);
                setDocumentHighlight(null);
              }}
              onChange={(e) => setDocumentHighlightReplacement(e.target.value)}
            />
            <div
              className="comment_text-highlight-delete"
              onClick={onDeleteDocumentHighlight}
            >
              <CrossSvg />
            </div>
          </div>
        )}
        <div className="comment_editor_content">
          {user && (
            <div className="user_avatar">
              <UserAvatar
                isActive={true}
                size={24}
                src={avatarIcon as string}
                userEmail={user.email}
                userName={user.name}
              />
            </div>
          )}
          <MentionsInput
            inputRef={inputRef}
            autoFocus={autoFocus}
            allowSpaceInQuery
            disabled={!user}
            value={commentText}
            placeholder={
              (editorFor === 'new' && 'Write a comment..') ||
              (editorFor === 'reply' && 'Write a reply..') ||
              ''
            }
            onChange={(e) => {
              setCommentText(e.target.value);
              setCommentTextState(e.target.value);
              onChangePrevValues(e.target.value);
            }}
            onFocus={() => {
              setIsCommentFieldFocused(true);
            }}
            onBlur={(e) => {
              searchPotentialMentions('');
              const next = e.relatedTarget as HTMLElement | null;
              if (
                next?.closest(
                  '.comment_input__input, .comment_text-highlight-input'
                )
              ) {
                return;
              }
              setIsCommentFieldFocused(false);
            }}
            onKeyDown={async (event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                setOnAddNewMention(false);
                if (isCommentSending || onAddNewMention) return;
                if (
                  event.key === 'Enter' &&
                  !event.shiftKey &&
                  commentText.length
                ) {
                  event.preventDefault();
                  setIsCommentSending(true);
                  await onSubmit();
                  setIsCommentSending(false);
                }
              }
            }}
            className="comment_input"
            suggestionsPortalHost={
              document.querySelector('.comment_editor_mentions_list') ||
              undefined
            }
          >
            <Mention
              trigger={commentMentions ? '@' : /^\b$/}
              markup="{{__id__}}"
              data={(q, callback) => {
                callback(mentions);
                searchPotentialMentions(q);
                mentionsCallbackRef.current = callback;
              }}
              displayTransform={(id) => {
                const mention = selectedMentions.find((el) => el.id === id);
                return mention ? `@${mention.name}` : '';
              }}
              onAdd={(id) => {
                const selectedMentions = mentions.filter((el) => el.id === id);
                searchPotentialMentions('');
                setSelectedMentions((prev) => [...prev, ...selectedMentions]);
                setOnAddNewMention(true);
              }}
              appendSpaceOnAdd={true}
              renderSuggestion={(entry) => {
                const mention = mentions.find((el) => el.id === entry.id);
                if (!mention) return null;
                return (
                  <div style={{ zIndex: 9 }} className="container">
                    {mention.picture}
                    <div className="info">
                      <div className="name">{mention.name}</div>
                      <div className="email">{mention.description}</div>
                    </div>
                  </div>
                );
              }}
            />
          </MentionsInput>
          <div className="comment_editor_mentions_list" />
          {!isFocused && (
            <SendCommentBtn
              isDisabled={true}
              editCommentId={editCommentId}
              setEditCommentId={setEditCommentId}
              editorFor={editorFor}
              replyToCommentId={replyToCommentId}
              setReplyToCommentId={setReplyToCommentId}
              onSubmit={onSubmit}
            />
          )}
        </div>
        {isFocused && !disableAttachments && !!commentFiles.length && (
          <CommentAttachments
            files={commentFiles as AttachmentDto[]}
            mode="upload"
            key={commentDetails?.id}
            onDeleteFile={(file) => {
              setCommentAttachments((prevCommentAttachments) =>
                prevCommentAttachments.filter((i) => i.id !== file.id)
              );
              uppy.removeFile(file.id);
            }}
            onAddFile={(file) => onAddFiles([file])}
          />
        )}
        {isFocused && (
          <div className="comment_editor_tools">
            <div className="comment_editor_tools__left">
              {!disableAttachments && (
                <Tooltip
                  placement="top"
                  title="Attachment media"
                  overlayClassName="media_viewer_tooltip"
                >
                  <Button
                    onClick={onOpenAttachmentModal}
                    className="btn-mention"
                  >
                    <AttachSvg className="b-white-icon" />
                  </Button>
                </Tooltip>
              )}
              <Tooltip
                placement="top"
                title="Mention someone"
                overlayClassName="media_viewer_tooltip"
              >
                <Dropdown
                  menu={usersMenu}
                  placement="bottom"
                  trigger={['click']}
                  overlayClassName="editor_overlay"
                  disabled={!potentialMentions.length}
                >
                  <Button className="btn-mention">
                    <DoggySvg />
                  </Button>
                </Dropdown>
              </Tooltip>
              {!commentMentions && (
                <UpgradePlanTooltip
                  text=" to a Team plan to unlock mentions."
                  isCanVisible
                >
                  <Button className="btn-mention">
                    <UpgradePlanSvg />
                  </Button>
                </UpgradePlanTooltip>
              )}

              {checkboxValueType && (
                <>
                  {checkboxValueType === 'timeCode' && (
                    <Tooltip
                      placement="bottom"
                      title="The annotation must be attached to the timecode."
                      overlayClassName="media_viewer_tooltip"
                    >
                      <Checkbox
                        onChange={(e) => {
                          setIsCheckboxChecked(e.target.checked);
                          setIsTimecodeChecked(e.target.checked);
                        }}
                        checked={isCheckboxChecked}
                        className="rich_text_form_checkbox"
                        disabled={hasAnnotations}
                      >
                        {renderCommentTime(playerTime[0] || 0)}
                        {playerTime[1] && (
                          <> - {renderCommentTime(playerTime[1])}</>
                        )}
                      </Checkbox>
                    </Tooltip>
                  )}
                  {checkboxValueType === 'pageNumber' && (
                    <Tooltip
                      placement="bottom"
                      title="The annotation must be attached to the page."
                      overlayClassName="media_viewer_tooltip"
                    >
                      <Checkbox
                        onChange={(e) => {
                          setIsCheckboxChecked(e.target.checked);
                          setIsTimecodeChecked(e.target.checked);
                        }}
                        checked={isCheckboxChecked}
                        className="rich_text_form_checkbox"
                        disabled={hasAnnotations || hasDocumentHighlight}
                      >
                        Page {activePageNumber}
                      </Checkbox>
                    </Tooltip>
                  )}
                </>
              )}
            </div>
            <div className="comment_editor_tools__right">
              <SendCommentBtn
                isDisabled={
                  !hasAnnotations && !commentText && !hasDocumentHighlight
                }
                editCommentId={editCommentId}
                setEditCommentId={setEditCommentId}
                editorFor={editorFor}
                replyToCommentId={replyToCommentId}
                setReplyToCommentId={setReplyToCommentId}
                onSubmit={onSubmit}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CommentEditor;
